import { useEffect, useState } from 'react';
import { publicSettings, whatsAppNumber } from '../api/publicSettings';
import { useGoBack } from '../lib/useGoBack';
import '../pages/bookRide.css';

/**
 * Help, as the InGo office answers it (Rio, 2 Oct 2026: support chat becomes
 * contact buttons): Call, WhatsApp and Email, from Settings, Branding, so the
 * office changes its number in one place. Replaces the old system's support
 * chat for clients and riders alike; `note` says what else is close at hand
 * (a rider's number on a client's delivery, for instance).
 *
 * `children` go below the office's buttons: the client's Help & support adds
 * its links there, so no page writes the office's details itself.
 *
 * @param {{ title?: string, note?: string, back?: string, children?: import('react').ReactNode }} props
 */
export default function SupportContacts({ title = 'Help and support', note = '', back = '/profile', children = null }) {
  const goBack = useGoBack(back);
  const [contacts, setContacts] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let gone = false;
    publicSettings.contacts().then(
      (c) => !gone && setContacts(c),
      () => !gone && setFailed(true),
    );

    return () => {
      gone = true;
    };
  }, []);

  // A number written locally is the home country's (InGo's Settings, Regional).
  const whatsApp = contacts?.phone ? whatsAppNumber(contacts.phone, contacts.dialCode) : null;
  const none = contacts && !contacts.phone && !contacts.email;

  return (
    <div className="br-page br-page--form">
      <header className="br-nav br-nav--stacked">
        <button type="button" className="br-nav__back" aria-label="Back" onClick={goBack}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M15.5 18.5L8.5 12l7-7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="br-nav__center">
          <h1 className="br-nav__title">{title}</h1>
        </div>
        <span className="br-nav__spacer" aria-hidden />
      </header>
      <div className="br-scroll br-scroll--form">
        <section className="br-pd-card" aria-label="Contact the office">
          <h2 className="br-pd-heading">Talk to the {contacts?.appName || 'InGo'} office</h2>
          {!contacts && !failed ? (
            <p className="br-pe-service__sub" role="status">
              Finding the office's numbers…
            </p>
          ) : null}
          {failed ? (
            <p className="br-pe-service__sub" role="alert">
              The office's numbers could not be loaded. Check your connection and open this page again.
            </p>
          ) : null}
          {none ? <p className="br-pe-service__sub">The office has not published a number yet.</p> : null}
          {contacts?.phone ? (
            <a className="br-confirm br-confirm--link" style={{ marginTop: 12 }} href={`tel:${contacts.phone}`}>
              Call {contacts.phone}
            </a>
          ) : null}
          {whatsApp ? (
            <a className="br-confirm br-confirm--link" style={{ marginTop: 12 }} href={`https://wa.me/${whatsApp}`} target="_blank" rel="noopener noreferrer">
              WhatsApp the office
            </a>
          ) : null}
          {contacts?.email ? (
            <a className="br-confirm br-confirm--link" style={{ marginTop: 12 }} href={`mailto:${contacts.email}`}>
              Email {contacts.email}
            </a>
          ) : null}
          {note ? <p className="br-pe-service__sub" style={{ marginTop: 16 }}>{note}</p> : null}
        </section>
        {children}
      </div>
    </div>
  );
}
